/**
 * Message tokens (CHN-02 to CHN-04).
 */
import type { OfferDelivery, RewardType } from '../data/types';
import { TIERS_ENABLED } from '../config';

export interface TokenContext {
  rewardType: RewardType;
  delivery?: OfferDelivery;
  loyaltyActive: boolean;
}

export interface TokenDef {
  key: string;
  label: string;
  sample: string;
  /** Average length after personalisation, for the SMS length estimate. */
  avgLength: number;
  /** Why the token cannot be resolved in this context, or null when it can. */
  unresolvable?: (ctx: TokenContext) => string | null;
}

const needsLoyalty = (ctx: TokenContext) => (ctx.loyaltyActive ? null : 'needs an active loyalty program');

/** CHN-02 */
export const TOKENS: TokenDef[] = [
  { key: 'first_name', label: 'First name', sample: 'Maya', avgLength: 6 },
  { key: 'brand_name', label: 'Brand name', sample: 'A2B', avgLength: 10 },
  { key: 'outlet_name', label: 'Outlet name', sample: 'Princeton', avgLength: 12 },
  {
    key: 'reward_code', label: 'Reward code', sample: 'K7M4PX9T', avgLength: 8,
    unresolvable: (ctx) => (ctx.rewardType === 'offer' && ctx.delivery === 'unique_code' ? null : 'needs an offer reward delivered as a unique code'),
  },
  {
    key: 'reward_expiry', label: 'Reward expiry date', sample: 'Oct 16', avgLength: 6,
    unresolvable: (ctx) => (['offer', 'points_multiplier', 'free_reward'].includes(ctx.rewardType) ? null : 'needs a reward with a validity period'),
  },
  { key: 'points_balance', label: 'Points balance', sample: '320', avgLength: 4, unresolvable: needsLoyalty },
  {
    key: 'loyalty_tier', label: 'Loyalty tier', sample: 'Gold', avgLength: 6,
    unresolvable: (ctx) => (TIERS_ENABLED ? needsLoyalty(ctx) : 'loyalty tiers are not available'),
  },
  { key: 'link', label: 'Tracked link', sample: 'brsq.co/a8Kd2', avgLength: 14 },
  { key: 'app_link', label: 'Get the app link', sample: 'brsq.co/app-x7Q2', avgLength: 16 },
];

const TOKEN_PATTERN = /\{\{\s*([^{}]*?)\s*\}\}/g;

export function getToken(key: string): TokenDef | undefined {
  return TOKENS.find((t) => t.key === key);
}

/** Tokens offered in the picker: those that resolve for the campaign's reward type. */
export function availableTokens(ctx: TokenContext): TokenDef[] {
  return TOKENS.filter((t) => !t.unresolvable || t.unresolvable(ctx) === null);
}

export function extractTokens(text: string): string[] {
  return Array.from(text.matchAll(TOKEN_PATTERN), (m) => m[1]);
}

export interface TokenError {
  token: string;
  reason: 'unknown' | 'unresolvable';
  message: string;
}

/**
 * CHN-04 — unknown tokens fail validation.
 * CHN-03 — a known token that cannot be resolved for the reward type fails; `first_name` always resolves (falls back to "there").
 */
export function findTokenErrors(text: string, ctx: TokenContext): TokenError[] {
  const seen = new Set<string>();
  const errors: TokenError[] = [];
  for (const token of extractTokens(text)) {
    if (seen.has(token)) continue;
    seen.add(token);
    const def = getToken(token);
    if (!def) {
      errors.push({ token, reason: 'unknown', message: `{{${token}}} is not a supported token` });
      continue;
    }
    const why = def.unresolvable?.(ctx) ?? null;
    if (why) errors.push({ token, reason: 'unresolvable', message: `{{${token}}} ${why}` });
  }
  return errors;
}

/** Replaces known tokens with sample values, for previews and test sends (CMP-07). */
export function expandSample(text: string, overrides: Record<string, string> = {}): string {
  return text.replace(TOKEN_PATTERN, (whole, key: string) => overrides[key] ?? getToken(key)?.sample ?? whole);
}

/** Replaces known tokens with average-length filler, for length estimates (CHN-06). */
export function expandForEstimate(text: string): string {
  return text.replace(TOKEN_PATTERN, (whole, key: string) => {
    const def = getToken(key);
    return def ? 'x'.repeat(def.avgLength) : whole;
  });
}
